import { useState, useEffect } from "react";
import { api } from "./api";
import { ClientForm } from "./ClientForm";

interface DashboardProps {
  onLogout: () => void;
}

export function Dashboard({ onLogout }: DashboardProps) {
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [error, setError] = useState("");

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await api.getClients();
      setClients(data);
    } catch (err: any) {
      setError("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleCreate = () => {
    setEditingClient(null);
    setIsFormOpen(true);
  };

  const handleEdit = (client: any) => {
    setEditingClient(client);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this client?")) return;
    try {
      await api.deleteClient(id);
      fetchClients();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleToggleStatus = async (client: any) => {
    try {
      await api.updateClient(client._id, {
        status: client.status === 'active' ? 'inactive' : 'active'
      });
      fetchClients();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div>
          <h1 className="text-4xl font-bold tracking-tight bg-linear-to-r from-white to-white/40 bg-clip-text text-transparent">
            Client Management
          </h1>
          <p className="text-white/40 mt-1 font-medium">Manage your multi-tenant profiles and configurations</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={handleCreate}
            className="px-6 py-3 bg-white text-black font-bold rounded-2xl hover:bg-neutral-200 active:scale-95 transition-all shadow-lg shadow-white/5"
          >
            Add Client
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl flex items-center gap-3">
           <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
           {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-50">
          <div className="w-10 h-10 border-4 border-white/10 border-t-white rounded-full animate-spin" />
          <p className="text-sm font-medium tracking-widest uppercase">Loading Clients</p>
        </div>
      ) : (
          <div className="grid gap-6">
            <div className="glass-dark rounded-[2.5rem] border border-white/10 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/5 bg-white/2">
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/30">Client Name</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/30">Code</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/30">Backend URL</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/30">Status</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-widest text-white/30 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {clients.map((client) => (
                      <tr key={client._id} className="group hover:bg-white/2 transition-colors">
                        <td className="px-8 py-6">
                          <div className="font-bold text-lg group-hover:text-purple-400 transition-colors">{client.name}</div>
                          <div className="text-xs text-white/30 font-mono mt-1">{client.databaseName || 'No Database'}</div>
                        </td>
                        <td className="px-8 py-6">
                          <span className="bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-wider">
                            {client.clientCode}
                          </span>
                        </td>
                        <td className="px-8 py-6 text-sm text-white/50 font-medium">
                          {client.backendUrl}
                        </td>
                        <td className="px-8 py-6">
                          <button
                            onClick={() => handleToggleStatus(client)}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter border transition-all ${
                              client.status === 'active'
                                ? 'bg-green-500/10 border-green-500/30 text-green-400'
                                : 'bg-red-500/10 border-red-500/30 text-red-400'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${client.status === 'active' ? 'bg-green-400' : 'bg-red-400'}`} />
                            {client.status}
                          </button>
                        </td>
                        <td className="px-8 py-6 text-right">
                          <div className="flex justify-end gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEdit(client)}
                              className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/60 hover:text-white"
                              title="Edit Client"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(client._id)}
                              className="p-2 hover:bg-red-500/20 rounded-xl transition-colors text-white/60 hover:text-red-400"
                              title="Delete Client"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {clients.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-8 py-20 text-center text-white/20 font-medium">
                          No clients found. Click "Add Client" to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      {isFormOpen && (
        <ClientForm
          client={editingClient}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => {
            setIsFormOpen(false);
            fetchClients();
          }}
        />
      )}
    </div>
  );
}
